import {
  Component,
  AfterViewInit,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  viewChild,
  PLATFORM_ID,
} from '@angular/core';
import * as echarts from 'echarts';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { IconFieldModule } from 'primeng/iconfield';
import { ChangeDetectorRef } from '@angular/core';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuItem } from 'primeng/api';
import { SkeletonModule } from 'primeng/skeleton';
import { FormArray, FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { Core } from '../_core/core';
import { HomeS } from '../_core/home';
import { Validators } from '@angular/forms';
import { Spinner } from '../_core/spinner';
import { ReactiveFormsModule } from '@angular/forms';
import { CarouselModule } from 'primeng/carousel';
import { Metering, MonthlyChartFingerprint } from '../_core/metering';
import {
  downloadTableFile,
  exportFilename,
  exportNumberCell,
  exportTextCell,
  ExportFormat,
  ExportTable,
} from '../utils/table-file-export';

interface SupplyType {
  supplyTypeId: number;
  supplyName: string;
  readingPlannedDate?: string;
}

@Component({
  selector: 'app-readings',
  imports: [
    CommonModule,
    TooltipModule,
    CarouselModule,
    SelectModule,
    DatePickerModule,
    ReactiveFormsModule,
    LucideAngularModule,
    TableModule,
    CardModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    PopoverModule,
    ButtonModule,
    SplitButtonModule,
    SkeletonModule,
    FormsModule,
    MessageModule,
  ],

  templateUrl: './readings.html',
  styleUrl: './readings.scss',
})
export class Readings implements OnInit, AfterViewInit, OnDestroy {
  private chartContainerRef = viewChild<ElementRef<HTMLElement>>('chartContainer');
  private chartContainer2Ref = viewChild<ElementRef<HTMLElement>>('chartContainer2');
  private chartContainerDistributedRef = viewChild<ElementRef<HTMLElement>>(
    'chartContainerDistributed',
  );
  private $spinner = inject(Spinner);
  private $core = inject(Core);
  private isSyncingSlider = false;
  private cdr = inject(ChangeDetectorRef);
  private $home = inject(HomeS);
  private $readingsService = inject(Metering);
  private readonly platformId = inject(PLATFORM_ID);
  public isDownloading = signal(false);
  readonly exportMenuItems: MenuItem[] = [
    { label: 'CSV', icon: 'pi pi-file', command: () => void this.exportConsumptions('csv') },
    {
      label: 'Excel',
      icon: 'pi pi-file-excel',
      command: () => void this.exportConsumptions('xlsx'),
    },
  ];
  /** Solo para la tabla «Listado de consumos y lecturas» (viewport < 768px). */
  public readonly isNarrowViewport = signal(false);
  private narrowViewportQuery: MediaQueryList | null = null;
  private narrowViewportListener: ((event: MediaQueryListEvent) => void) | null = null;
  public homeData = this.$home.homeData;

  private previousFormatType = 'Consumo individual';
  public distributionType = ['Consumo desglosado', 'Consumo individual'];

  public supplies = this.$readingsService.supplies;
  public meters = this.$readingsService.meters;
  public consumptions = this.$readingsService.consumptions;
  public consumptionsUnits = this.$readingsService.consumptionUnits;
  public consumptionsDistributed = this.$readingsService.consumptionsDistributed;
  public consumptionsUnitsDistributed = this.$readingsService.consumptionUnitsByLocation;

  public consumptionsTable = this.$readingsService.consumptionsTable;
  public consumptionsTableByMeter = this.$readingsService.consumptionsTableByMeter;
  public totalRecords = this.$readingsService.totalElements;
  public useFixedConsumptionsTableRowHeight = computed(
    () => this.consumptionsTable().length > 0 && this.consumptionsTable().length < 8,
  );
  public useFixedConsumptionsByMeterTableRowHeight = computed(
    () => this.consumptionsTableByMeter().length > 0 && this.consumptionsTableByMeter().length < 8,
  );
  public first: number = 0;
  public rows: number = 10;

  public tenantSelected: any;

  public maxDate: Date = new Date();
  public minDate: Date = new Date();

  public configForm!: FormGroup;
  get comparativas() {
    return this.configForm.get('comparativas') as FormArray;
  }
  public currentPage = signal<number>(0);

  public carouselPage: number = 0;
  public filteredMeters = signal<any[]>([]);

  public resumen = {
    avg: '0,00',
    maxV: '0,00',
    maxLabel: '---',
    minV: '0,00',
    minLabel: '---',
    total: '0,00',
    units: '',
  };
  public resumenDistributed = {
    avg: '0,00',
    maxV: '0,00',
    maxLabel: '---',
    minV: '0,00',
    minLabel: '---',
    total: '0,00',
    units: '',
  };

  public showCalendar: boolean = false;
  public showDateRangeYearWarning = signal(false);
  public loading: boolean = false;
  public initialLoading = signal(true);
  private initialTableLoad = true;
  private lazyFiredDuringBootstrap = false;

  public predefinedDateOptions = [
    {
      label: 'Últimos 30 días',
      value: {
        startDate: this.getDateDaysAgo(30),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 60 días',
      value: {
        startDate: this.getDateMonthsAgo(2),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 6 meses',
      value: {
        startDate: this.getDateMonthsAgo(6),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 12 meses',
      value: {
        startDate: this.getDateYearsAgo(1),
        endDate: this.formatDate(new Date()),
      },
    },
    {
      label: 'Últimos 24 meses',
      value: {
        startDate: this.getDateYearsAgo(2),
        endDate: this.formatDate(new Date()),
      },
    },
  ];

  constructor(private fb: FormBuilder) {}
  setMaxMinDates() {
    this.maxDate = new Date();
    this.minDate = new Date(this.maxDate);
    this.minDate.setFullYear(this.maxDate.getFullYear() - 5);
  }
  async ngOnInit() {
    this.initNarrowViewportListener();
    this.initForms();
    this.setMaxMinDates();

    this.tenantSelected = this.$core.selectedBuilding();
    if (!this.tenantSelected) {
      this.tenantSelected = await this.$core.getTenantFromLocal();
    }

    if (!this.isDashboardPrefetched()) {
      await this.$home.fetchHomeData();
    }

    if (!this.areSuppliesPrefetched()) {
      await this.$readingsService.getSupplies(this.tenantSelected!.tenantId);
    }

    const currentSupplies = this.supplies();
    let cacheHit = false;

    if (currentSupplies && currentSupplies.length > 0) {
      const firstSupply = currentSupplies[0];
      this.configForm.get('supplyType')?.setValue(firstSupply, { emitEvent: false });

      const supplyTypeId = this.getSupplyTypeId(firstSupply);
      if (!this.$readingsService.hasMetersForSupply(supplyTypeId)) {
        await this.$readingsService.getMeters(this.tenantSelected!.tenantId, supplyTypeId);
      }

      const monthlyFingerprint = this.buildMonthlyBootstrapFingerprint(firstSupply);
      cacheHit = this.$readingsService.matchesMonthlyChartFingerprint(monthlyFingerprint);

      if (!cacheHit) {
        this.beginInitialPageLoad();
      }

      if (cacheHit) {
        await this.hydrateMonthlyBootstrap(firstSupply);
        await this.createChart();
      } else {
        await this.onSupplyTypeChange({ value: firstSupply }, true);
      }
    }

    if (!cacheHit && currentSupplies?.length) {
      this.endInitialPageLoad();
    } else if (!currentSupplies?.length) {
      this.initialLoading.set(false);
    } else {
      this.initialLoading.set(false);
    }
  }

  private isDashboardPrefetched(): boolean {
    return this.homeData() !== null;
  }

  private areSuppliesPrefetched(): boolean {
    return (
      this.$readingsService.isForTenant(this.tenantSelected!.tenantId) && this.supplies().length > 0
    );
  }

  private getSupplyTypeId(supply: { supplyTypeId?: number; id?: number }): number {
    return supply.supplyTypeId ?? supply.id ?? 0;
  }

  private isTablePrefetched(): boolean {
    const tenantId = this.tenantSelected?.tenantId;
    const supply = this.configForm?.get('supplyType')?.value;
    const meter = this.configForm?.get('meter')?.value;
    const range = this.getSelectedDateRange();
    if (!tenantId || !supply || !meter || !range) {
      return false;
    }

    return this.$readingsService.isReusableMonthlyTableCache(
      tenantId,
      this.getSupplyTypeId(supply),
      meter.meterId,
      range.dateStart,
      range.dateEnd,
    );
  }

  private isDistributedTablePrefetched(): boolean {
    const tenantId = this.tenantSelected?.tenantId;
    const supply = this.configForm?.get('supplyType')?.value;
    const range = this.getSelectedDateRange();
    if (!tenantId || !supply || !range) {
      return false;
    }

    return this.$readingsService.isReusableMonthlyDistributedTableCache(
      tenantId,
      this.getSupplyTypeId(supply),
      range.dateStart,
      range.dateEnd,
    );
  }

  private getSelectedDateRange(): { dateStart: string; dateEnd: string } | null {
    if (!this.configForm) {
      return null;
    }

    if (this.showCalendar) {
      const { inicio, fin } = this.comparativas.at(0).value;
      if (!inicio || !fin) {
        return null;
      }
      return { dateStart: this.formatDate(inicio), dateEnd: this.formatDate(fin) };
    }

    const range = this.configForm.get('predefineDateOption')?.value?.value;
    if (!range?.startDate || !range?.endDate) {
      return null;
    }

    return { dateStart: range.startDate, dateEnd: range.endDate };
  }

  private getBootstrapDateStrings(): { from: string; to: string } {
    const range = this.getSelectedDateRange();
    return { from: range?.dateStart ?? '', to: range?.dateEnd ?? '' };
  }

  private buildMonthlyBootstrapFingerprint(
    supply: { supplyTypeId?: number; id?: number },
    meterId = this.meters()[0]?.meterId ?? 0,
  ): MonthlyChartFingerprint {
    const { from, to } = this.getBootstrapDateStrings();

    return {
      tenantId: this.tenantSelected!.tenantId,
      supplyTypeId: this.getSupplyTypeId(supply),
      meterId,
      frequency: 'MONTH',
      from,
      to,
    };
  }

  private async hydrateMonthlyBootstrap(supply: {
    supplyTypeId?: number;
    id?: number;
  }): Promise<void> {
    const supplyTypeId = this.getSupplyTypeId(supply);
    const currentMeters = this.meters();
    if (!currentMeters.length) {
      return;
    }

    this.filteredMeters.set(currentMeters);
    const firstMeter = currentMeters[0];
    const meterControl = this.configForm.get('meter');
    meterControl?.enable();
    meterControl?.setValue(firstMeter, { emitEvent: false });

    const { from, to } = this.getBootstrapDateStrings();
    const fingerprint = this.buildMonthlyBootstrapFingerprint(supply, firstMeter.meterId);

    if (!this.isTablePrefetched()) {
      await this.$readingsService.getConsumptionsTable(
        {
          tenantId: this.tenantSelected!.tenantId,
          supplyTypeId,
          meterId: firstMeter.meterId,
          frequency: 'MONTH',
          from,
          to,
          page: 0,
          size: this.rows,
          sortField: 'consumptionDate',
          sort: 'desc',
        },
        { fingerprint },
      );
      this.syncTableTotalFromFullConsumptions();
    }

    if (supplyTypeId === 14 && this.consumptionsDistributed().length === 0) {
      await this.$readingsService.getConsumptionsByLocation(
        this.tenantSelected!.tenantId,
        supplyTypeId,
        from,
        to,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
    }
  }

  private beginInitialPageLoad(): void {
    this.$spinner.show();
    this.loading = true;
    this.initialLoading.set(true);
    this.first = 0;
    this.initialTableLoad = true;
    this.lazyFiredDuringBootstrap = false;
  }

  private endInitialPageLoad(): void {
    this.initialLoading.set(false);
    this.loading = false;
    this.$spinner.hide();
    if (this.lazyFiredDuringBootstrap && this.isTablePrefetched()) {
      this.initialTableLoad = false;
    }
  }

  private endUserLoading(): void {
    this.loading = false;
    this.$spinner.hide();
  }

  ngAfterViewInit() {}

  initForms() {
    const { from, to } = this.$readingsService.getDefaultMonthlyRange();
    const defaultOption = {
      label: 'Últimos 24 meses',
      value: { startDate: from, endDate: to },
    };
    this.configForm = this.fb.group({
      supplyType: [null, Validators.required],
      meter: [{ value: null, disabled: true }, Validators.required],
      formatType: ['Consumo individual', Validators.required],
      comparativas: this.fb.array([this.createDateGroup()]),
      predefineDateOption: [defaultOption],
    });

    this.configForm.get('meter')?.valueChanges.subscribe((meter) => {
      if (meter) {
        const index = this.filteredMeters().findIndex((m) => m.meterId === meter.meterId);
        if (index !== -1 && index !== this.currentPage()) {
          this.isSyncingSlider = true;
          this.currentPage.set(index);

          setTimeout(() => {
            this.isSyncingSlider = false;
          });
        }
      }
    });
    this.configForm.get('supplyType')?.valueChanges.subscribe(() => {
      this.currentPage.set(0);
    });
  }

  async onSupplyTypeChange(event: any, isBootstrap = false) {
    if (!isBootstrap) {
      this.$spinner.show();
      this.loading = true;
    }

    const supplyTypeId = this.getSupplyTypeId(event.value);
    const meterControl = this.configForm.get('meter');

    try {
      if (supplyTypeId) {
        if (!this.$readingsService.hasMetersForSupply(supplyTypeId)) {
          await this.$readingsService.getMeters(this.tenantSelected!.tenantId, supplyTypeId);
        }

        if (!isBootstrap) {
          this.$spinner.show();
          this.loading = true;
        }

        const currentMeters = this.meters();

        if (currentMeters && currentMeters.length > 0) {
          this.filteredMeters.set(currentMeters || []);
          const firstMeter = currentMeters[0];
          meterControl?.enable();
          meterControl?.setValue(firstMeter, { emitEvent: false });

          const range = this.getSelectedDateRange();
          if (!range) {
            return;
          }

          const { dateStart: from, dateEnd: to } = range;
          const monthlyFingerprint = isBootstrap
            ? this.buildMonthlyBootstrapFingerprint(event.value, firstMeter.meterId)
            : undefined;
          const fetchOptions = monthlyFingerprint ? { fingerprint: monthlyFingerprint } : undefined;
          const consumptionParams = {
            tenantId: this.tenantSelected!.tenantId,
            supplyTypeId,
            meterId: firstMeter.meterId,
            frequency: 'MONTH',
            from,
            to,
          };
          const tableParams = {
            ...consumptionParams,
            page: 0,
            size: this.rows,
            sortField: 'consumptionDate',
            sort: 'desc' as const,
          };

          if (supplyTypeId != 14) {
            await Promise.all([
              this.$readingsService.getConsumptions(consumptionParams, fetchOptions),
              this.$readingsService.getConsumptionsTable(tableParams, fetchOptions),
            ]);
          } else {
            await Promise.all([
              this.$readingsService.getConsumptions(consumptionParams, fetchOptions),
              this.$readingsService.getConsumptionsTable(tableParams, fetchOptions),
            ]);
            await this.$readingsService.getConsumptionsByLocation(
              this.tenantSelected!.tenantId,
              supplyTypeId,
              from,
              to,
              undefined,
              undefined,
              undefined,
              undefined,
              !isBootstrap,
            );
          }

          this.syncTableTotalFromFullConsumptions();

          await this.createChart();
        } else {
          this.consumptions.set([]);
          this.consumptionsUnits.set('');
          this.consumptionsTable.set([]);
          this.$readingsService.totalElementsSignal.set(0);
          this.$readingsService.totalPagesSignal.set(0);
          this.clearSummarys();

          meterControl?.setValue(null);
          meterControl?.disable();
        }
      } else {
        meterControl?.setValue(null);
        meterControl?.disable();
      }
    } finally {
      if (isBootstrap) {
        this.endInitialPageLoad();
      } else {
        this.endUserLoading();
      }
    }
  }
  async loadConsumptionsLazy(event: any) {
    if (this.initialLoading()) {
      this.lazyFiredDuringBootstrap = true;
      this.first = 0;
      this.rows = event.rows ?? this.rows;
      return;
    }

    if (this.initialTableLoad && this.isTablePrefetched()) {
      const nextRows = event.rows ?? this.rows;
      const nextFirst = event.first ?? 0;
      // Solo salta el onLazyLoad inicial (mismos rows/página). Un cambio de page-size debe pedir datos.
      if (nextRows === this.rows && nextFirst === 0) {
        this.initialTableLoad = false;
        return;
      }
      this.initialTableLoad = false;
    }

    const supply = this.configForm.get('supplyType')?.value;
    const meter = this.configForm.get('meter')?.value;
    const range = this.getSelectedDateRange();

    if (this.initialTableLoad) {
      this.first = 0;
      this.rows = event.rows ?? this.rows;
    } else {
      this.first = event.first ?? 0;
      this.rows = event.rows ?? this.rows;
    }

    const pageToLoad = Math.floor(this.first / this.rows);
    const sortField = (event.sortField as string) ?? 'consumptionDate';

    let sortOrder = 'desc';
    if (event.sortOrder === 1) sortOrder = 'asc';
    if (event.sortOrder === -1) sortOrder = 'desc';

    if (this.tenantSelected && supply && meter && range) {
      const showSpinner = !this.initialTableLoad;
      this.initialTableLoad = false;

      if (showSpinner) {
        this.$spinner.show();
      }

      try {
        await this.$readingsService.getConsumptionsTable({
          tenantId: this.tenantSelected.tenantId,
          supplyTypeId: this.getSupplyTypeId(supply),
          meterId: meter.meterId,
          frequency: 'MONTH',
          from: range.dateStart,
          to: range.dateEnd,
          page: pageToLoad,
          size: this.rows,
          sortField: sortField,
          sort: sortOrder,
        });

        this.syncTableTotalFromFullConsumptions();
        await this.createChart();
      } catch (error) {
        if (showSpinner) {
          this.$spinner.hide();
        }
      } finally {
        if (showSpinner) {
          this.$spinner.hide();
        }
      }
    }
  }
  async loadConsumptionsLazyDistributedByMeter(event: any) {
    if (this.initialLoading()) {
      this.lazyFiredDuringBootstrap = true;
      this.first = 0;
      this.rows = event.rows ?? this.rows;
      return;
    }

    if (this.initialTableLoad && this.isDistributedTablePrefetched()) {
      const nextRows = event.rows ?? this.rows;
      const nextFirst = event.first ?? 0;
      if (nextRows === this.rows && nextFirst === 0) {
        this.initialTableLoad = false;
        return;
      }
      this.initialTableLoad = false;
    }

    const supply = this.configForm.get('supplyType')?.value;
    const range = this.getSelectedDateRange();

    if (this.initialTableLoad) {
      this.first = 0;
      this.rows = event.rows ?? this.rows;
    } else {
      this.first = event.first ?? 0;
      this.rows = event.rows ?? this.rows;
    }

    const pageToLoad = Math.floor(this.first / this.rows);
    const sortField = (event.sortField as string) ?? 'meterId';

    let sortOrder: string | undefined = undefined;
    if (event.sortOrder === 1) sortOrder = 'asc';
    if (event.sortOrder === -1) sortOrder = 'desc';

    if (this.tenantSelected && supply && range) {
      const showSpinner = !this.initialTableLoad;
      this.initialTableLoad = false;

      if (showSpinner) {
        this.$spinner.show();
      }

      try {
        await this.$readingsService.getConsumptionsByLocationByMeterTable(
          this.tenantSelected.tenantId,
          this.getSupplyTypeId(supply),
          range.dateStart,
          range.dateEnd,
          pageToLoad,
          this.rows,
          sortField,
          sortOrder,
        );

        await this.createChart();
      } catch (error) {
        if (showSpinner) {
          this.$spinner.hide();
        }
      } finally {
        if (showSpinner) {
          this.$spinner.hide();
        }
      }
    }
  }

  private async refreshMonthlyTable(
    page = 0,
    size = this.rows,
    sortField = 'consumptionDate',
    sort = 'desc',
  ): Promise<void> {
    const tenantId = this.tenantSelected?.tenantId;
    const supply = this.configForm.get('supplyType')?.value;
    const meter = this.configForm.get('meter')?.value;
    const range = this.getSelectedDateRange();
    if (!tenantId || !supply || !meter || !range) {
      return;
    }

    await this.$readingsService.getConsumptionsTable({
      tenantId,
      supplyTypeId: this.getSupplyTypeId(supply),
      meterId: meter.meterId,
      frequency: 'MONTH',
      from: range.dateStart,
      to: range.dateEnd,
      page,
      size,
      sortField,
      sort,
    });
    this.syncTableTotalFromFullConsumptions();
  }

  /** Asegura que el paginador use el total real, no solo los ítems de la página. */
  private syncTableTotalFromFullConsumptions(): void {
    const fullCount = this.consumptions().length;
    const currentTotal = this.totalRecords();
    if (fullCount > currentTotal) {
      this.$readingsService.totalElementsSignal.set(fullCount);
      this.$readingsService.totalPagesSignal.set(
        Math.max(1, Math.ceil(fullCount / Math.max(this.rows, 1))),
      );
    }
  }

  async getConsumptions() {
    const supply = this.configForm.get('supplyType')?.value;
    const meter = this.configForm.get('meter')?.value;
    const range = this.getSelectedDateRange();

    if (this.tenantSelected && supply && meter && range) {
      this.$spinner.show();
      this.loading = true;
      try {
        await Promise.all([
          this.$readingsService.getConsumptions({
            tenantId: this.tenantSelected.tenantId,
            supplyTypeId: this.getSupplyTypeId(supply),
            meterId: meter.meterId,
            frequency: 'MONTH',
            from: range.dateStart,
            to: range.dateEnd,
          }),
          this.refreshMonthlyTable(0, this.rows),
        ]);

        this.syncTableTotalFromFullConsumptions();
        await this.createChart();
      } catch (error) {
        this.$spinner.hide();
        this.loading = false;
      } finally {
        this.$spinner.hide();
        this.loading = false;
      }
    }
  }
  async onMeterChange(event: any) {
    if (event.value) {
      this.first = 0;
      this.initialTableLoad = false;
      await this.getConsumptions();
    }
  }
  switchCalendar() {
    this.clearDateRangeYearWarning();
    // Manual → pull-down: keep last predefined selection and search
    if (this.showCalendar === true) {
      this.showCalendar = false;
      void this.applyConfig();
      return;
    }

    // Pull-down → manual: preload same dates, do not search
    const range = this.configForm.get('predefineDateOption')?.value?.value as
      { startDate?: string; endDate?: string } | undefined;
    if (range?.startDate && range?.endDate) {
      const inicio = this.parseDateString(range.startDate);
      const fin = this.parseDateString(range.endDate);
      if (inicio && fin) {
        this.comparativas.at(0).patchValue({ inicio, fin }, { emitEvent: false });
      }
    }
    this.showCalendar = true;
  }

  private parseDateString(value: string): Date | null {
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  onDateChange(index: number, field: 'inicio' | 'fin') {
    const group = this.comparativas.at(index);
    let { inicio, fin } = group.value;
    if (!(inicio instanceof Date) || !(fin instanceof Date)) return;

    const MAX_YEARS = 2;
    inicio = this.toDateOnly(inicio);
    fin = this.toDateOnly(fin);
    const maxAllowed = this.toDateOnly(this.maxDate);
    const minAllowed = this.toDateOnly(this.minDate);

    if (field === 'inicio') {
      if (inicio > fin) {
        fin = new Date(inicio);
      }
      const maxFin = new Date(inicio);
      maxFin.setFullYear(inicio.getFullYear() + MAX_YEARS);
      if (fin > maxFin) {
        fin = maxFin;
      }
      if (fin > maxAllowed) {
        fin = new Date(maxAllowed);
      }
      // Evitar reescribir inicio (el datepicker ya lo acaba de setear).
      // Diferir Fin: si se hace en el mismo tick que cambia minDate, PrimeNG deja el input en blanco.
      const finToSet = fin;
      setTimeout(() => {
        group.get('fin')?.setValue(this.toDateOnly(finToSet), { emitEvent: false });
      });
      return;
    }

    if (fin < inicio) {
      inicio = new Date(fin);
    }
    const minInicio = new Date(fin);
    minInicio.setFullYear(fin.getFullYear() - MAX_YEARS);
    if (inicio < minInicio) {
      inicio = minInicio;
    }
    if (inicio < minAllowed) {
      inicio = new Date(minAllowed);
    }
    const inicioToSet = inicio;
    setTimeout(() => {
      group.get('inicio')?.setValue(this.toDateOnly(inicioToSet), { emitEvent: false });
    });
  }

  private toDateOnly(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  onInicioYearChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  onInicioMonthChange(event: { year?: number; month?: number }, index: number): void {
    this.updateDateRangeWarningFromPicker(event, index);
  }

  private updateDateRangeWarningFromPicker(
    event: { year?: number; month?: number },
    index: number,
  ): void {
    const fin = this.comparativas.at(index).get('fin')?.value;
    const inicioActual = this.comparativas.at(index).get('inicio')?.value;
    if (!(fin instanceof Date) || event.year == null || event.month == null) {
      this.showDateRangeYearWarning.set(false);
      return;
    }

    const day = inicioActual instanceof Date ? inicioActual.getDate() : 1;
    const lastDayOfMonth = new Date(event.year, event.month, 0).getDate();
    const provisionalInicio = new Date(event.year, event.month - 1, Math.min(day, lastDayOfMonth));

    const maxFin = new Date(provisionalInicio);
    maxFin.setFullYear(provisionalInicio.getFullYear() + 2);
    this.showDateRangeYearWarning.set(fin.getTime() > maxFin.getTime());
  }

  clearDateRangeYearWarning(): void {
    this.showDateRangeYearWarning.set(false);
  }

  async applyConfig() {
    if (this.configForm.invalid) return;

    const tenantSelected = this.tenantSelected || (await this.$core.getTenantFromLocal());
    if (!tenantSelected?.tenantId) return;

    const supply = this.configForm.get('supplyType')?.value;
    const meter = this.configForm.get('meter')?.value;
    const range = this.getSelectedDateRange();
    if (!range) return;

    this.first = 0;
    this.initialTableLoad = false;

    if (this.configForm.controls['formatType'].value !== 'Consumo desglosado') {
      if (supply && meter) {
        this.$spinner.show();
        this.loading = true;

        try {
          await Promise.all([
            this.$readingsService.getConsumptions({
              tenantId: tenantSelected.tenantId,
              supplyTypeId: this.getSupplyTypeId(supply),
              meterId: meter.meterId,
              frequency: 'MONTH',
              from: range.dateStart,
              to: range.dateEnd,
            }),
            this.refreshMonthlyTable(0, this.rows),
          ]);

          await this.createChart();
        } catch (error) {
          console.error('Error al aplicar filtros de consumo:', error);
          this.$spinner.hide();
          this.loading = false;
        } finally {
          this.$spinner.hide();
          this.loading = false;
        }
      }
    } else if (supply) {
      const supplyTypeId = this.getSupplyTypeId(supply);
      this.$spinner.show();
      this.loading = true;

      try {
        await Promise.all([
          this.$readingsService.getConsumptionsByLocation(
            tenantSelected.tenantId,
            supplyTypeId,
            range.dateStart,
            range.dateEnd,
            0,
            this.rows,
            'meterId',
            'asc',
            false,
          ),
          this.$readingsService.getConsumptionsByLocationByMeterTable(
            tenantSelected.tenantId,
            supplyTypeId,
            range.dateStart,
            range.dateEnd,
            0,
            this.rows,
            'meterId',
            'asc',
          ),
        ]);

        await this.createChart();
      } catch (error) {
        console.error('Error al aplicar filtros de consumo:', error);
        this.$spinner.hide();
        this.loading = false;
      } finally {
        this.$spinner.hide();
        this.loading = false;
      }
    }
  }
  resetConfig() {
    const hoy = new Date();
    const haceDosAnios = new Date();
    haceDosAnios.setFullYear(hoy.getFullYear() - 2);

    this.comparativas.at(0).patchValue({
      inicio: null,
      fin: null,
    });
    this.cdr.detectChanges();
  }

  async onSelectDistributionType(event: any) {
    const newFormatType = event.value;
    const oldFormatType = this.previousFormatType;

    this.previousFormatType = newFormatType;

    const changedToIndividual =
      oldFormatType === 'Consumo desglosado' && newFormatType === 'Consumo individual';

    if (changedToIndividual) {
      await this.getConsumptions();
    } else {
      await this.createChart();
    }
  }

  private static readonly CHART_MIN_HEIGHT_PX = 450;
  private static readonly CHART_DOM_TIMEOUT_MS = 3000;

  private nextAnimationFrame(): Promise<void> {
    return new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
  }

  private isChartElementVisible(el: HTMLElement): boolean {
    if (!el.isConnected) {
      return false;
    }
    if (el.hidden) {
      return false;
    }

    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    let parent: HTMLElement | null = el.parentElement;
    while (parent) {
      if (parent.hidden) {
        return false;
      }
      if (getComputedStyle(parent).display === 'none') {
        return false;
      }
      parent = parent.parentElement;
    }

    return el.clientWidth > 0 && el.clientHeight > 0;
  }

  private getActiveChartRef(): ElementRef<HTMLElement> | undefined {
    const supplyTypeId = this.configForm.controls['supplyType']?.value?.supplyTypeId;
    if (supplyTypeId != 14) {
      return this.chartContainerRef();
    }

    const formatType = this.configForm.controls['formatType'].value;
    if (formatType === 'Consumo desglosado') {
      return this.chartContainerDistributedRef();
    }

    return this.chartContainer2Ref();
  }

  private async waitForActiveChartDom(): Promise<HTMLElement | null> {
    const deadline = Date.now() + Readings.CHART_DOM_TIMEOUT_MS;

    while (Date.now() < deadline) {
      this.cdr.detectChanges();
      await this.nextAnimationFrame();

      const el = this.getActiveChartRef()?.nativeElement;
      if (el && this.isChartElementVisible(el)) {
        return el;
      }

      await new Promise((resolve) => setTimeout(resolve, 16));
    }

    const el = this.getActiveChartRef()?.nativeElement;
    if (!el) {
      return null;
    }

    if (!this.isChartElementVisible(el)) {
      el.style.minHeight = `${Readings.CHART_MIN_HEIGHT_PX}px`;
      this.cdr.detectChanges();
      await this.nextAnimationFrame();
    }

    return this.isChartElementVisible(el) ? el : el;
  }

  private flushChartLayout(chart: echarts.ECharts): void {
    requestAnimationFrame(() => {
      chart.resize();
      requestAnimationFrame(() => chart.resize());
    });
  }

  private attachMagicTypeLineColorHandler(
    chart: echarts.ECharts,
    option: echarts.EChartsOption,
  ): void {
    const lineColor = this.getColorByType();
    chart.on('magictypechanged', (...args: unknown[]) => {
      const params = args[0] as { currentType?: string };
      if (params.currentType === 'line') {
        chart.setOption({
          series: [
            {
              itemStyle: { color: lineColor },
              lineStyle: { color: lineColor, width: 2 },
              symbol: 'circle',
              symbolSize: 8,
            },
          ],
        });
        return;
      }
      if (params.currentType === 'bar') {
        chart.setOption({ series: option.series }, { replaceMerge: ['series'] });
      }
    });
  }

  async createChart(): Promise<void> {
    const data = this.consumptions();
    const dataDistributed = this.consumptionsDistributed();
    if (!data.length && !dataDistributed.length) {
      return;
    }

    this.cdr.detectChanges();
    await this.nextAnimationFrame();

    const supplyTypeId = this.configForm.controls['supplyType']?.value?.supplyTypeId;
    const chartDom = await this.waitForActiveChartDom();
    if (!chartDom) {
      return;
    }

    if (supplyTypeId != 14) {
      this.createSummary(data);
      this.renderConsumptionChart(chartDom, data);
      return;
    }

    const formatType = this.configForm.controls['formatType'].value;
    if (formatType === 'Consumo desglosado') {
      this.createSummaryDistributed(dataDistributed);
      this.renderInitConsumptionChartDistributed(chartDom, dataDistributed);
    } else {
      this.createSummary(data);
      this.renderConsumptionChart2(chartDom, data);
    }
  }

  private renderConsumptionChart(dom: HTMLElement, data: any) {
    const chartData = data;
    const units = this.$readingsService.consumptionUnits() || ''; // Obtenemos las unidades del signal

    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);

    const categories = chartData.map((item: any) => {
      const date = new Date(item.consumptionDate);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });
    const narrow = dom.clientWidth < 768;
    const values = chartData.map((item: any) => item.consumptionValue);
    const { inicio, fin } = this.comparativas.at(0).value;
    const periodLabel = `Desde el ${this.dateSpanishFormat(inicio)} hasta el ${this.dateSpanishFormat(fin)}`;
    const option: echarts.EChartsOption = {
      title: {
        text: `Evolución de tu consumo de ${this.configForm.controls['supplyType'].value.supplyName} `,
        left: narrow ? '3%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),
        textStyle: {
          color: '#1d1d1f',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold', // Cambia "600" por 'bold'
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
        subtextStyle: {
          color: this.getColorByType(),
        },
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',
        feature: {
          saveAsImage: { title: 'Descargar', name: 'Grafico_Consumos' },
          magicType: { type: ['line', 'bar'], title: { line: 'Líneas', bar: 'Barras' } },
          restore: { title: 'Reset' },
        },
      },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        {
          type: 'slider',
          height: 18,
          left: narrow ? '25%' : '10%',
          right: narrow ? '25%' : '10%',

          handleSize: '120%',
          fillerColor: this.getColorByTypeTransparency(),
          handleIcon: 'circle',
          brushSelect: false,
          textStyle: {
            fontSize: 12,
            fontWeight: 'bold',
          },
          handleStyle: {
            color: this.getColorByType(),
            shadowBlur: 1,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      ],
      grid: {
        top: '15%',
        bottom: '15%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const dataItem = chartData[p.dataIndex];
          const dynamicColor = this.getColorByType();
          return `
          <div style="font-weight:600; color:${dynamicColor}">${this.spanishFormat(dataItem.consumptionValue)} ${units}</div>
          <div style="font-size:11px; color:#86868b">${this.dateSpanishFormat(dataItem.consumptionDate)}</div>

        `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          margin: 15,
          formatter: (value: string) => `${this.formatByFrequencyGP(value)}`,
        },
      },
      yAxis: {
        scale: true,
        type: 'value',

        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => `${this.spanishFormat(value)} ${units}`,
        },
      },
      series: [
        {
          data: values,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: (params: any) => {
              return params.dataIndex === values.length - 1
                ? this.getColorByType()
                : this.getColorByTypeTransparency();
            },
            borderRadius: [6, 6, 0, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 0, 0, 0.02)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };

    myChart.setOption(option);
    this.attachMagicTypeLineColorHandler(myChart, option);
    this.flushChartLayout(myChart);
    window.addEventListener('resize', () => myChart.resize());
  }

  private renderConsumptionChart2(dom: HTMLElement, data: any) {
    const chartData = data;
    const units = this.$readingsService.consumptionUnits() || '';
    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);

    const categories = chartData.map((item: any) => {
      const date = new Date(item.consumptionDate);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    });
    const narrow = dom.clientWidth < 768;
    const values = chartData.map((item: any) => item.consumptionValue);
    const { inicio, fin } = this.comparativas.at(0).value;
    const periodLabel = `Desde el ${this.dateSpanishFormat(inicio)} hasta el ${this.dateSpanishFormat(fin)}`;
    const option: echarts.EChartsOption = {
      title: {
        text: `Evolución de tu consumo de ${this.configForm.controls['supplyType'].value.supplyName} `,
        left: narrow ? '5%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),

        textStyle: {
          color: '#1d1d1f',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold',
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
        subtextStyle: {
          color: this.getColorByType(),
        },
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',
        feature: {
          saveAsImage: { title: 'Descargar', name: 'Grafico_Consumos' },
          magicType: { type: ['line', 'bar'], title: { line: 'Líneas', bar: 'Barras' } },
          restore: { title: 'Reset' },
        },
      },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        {
          type: 'slider',
          height: 18,
          left: narrow ? '20%' : '10%',
          right: narrow ? '20%' : '10%',
          bottom: '5%',
          fillerColor: this.getColorByTypeTransparency(),
          handleStyle: { color: this.getColorByType() },
        },
      ],
      grid: {
        top: '15%',
        bottom: '15%',
        left: '5%',
        right: '5%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const dataItem = chartData[p.dataIndex];
          const dynamicColor = this.getColorByType();
          return `
          <div style="font-weight:600; color:${dynamicColor}">${this.spanishFormat(dataItem.consumptionValue)} ${units}</div>
          <div style="font-size:11px; color:#86868b"> ${this.dateSpanishFormat(dataItem.consumptionDate)}</div>

        `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          margin: 15,
          formatter: (value: string) => `${this.formatByFrequencyGP(value)}`,
        },
      },
      yAxis: {
        scale: true,
        /*    boundaryGap: [0, '5%'], */
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => `${this.spanishFormat(value)} ${units}`,
        },
      },
      series: [
        {
          data: values,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: (params: any) => {
              return params.dataIndex === values.length - 1
                ? this.getColorByType()
                : this.getColorByTypeTransparency();
            },
            borderRadius: [6, 6, 0, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 0, 0, 0.02)',
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };

    myChart.setOption(option);
    this.attachMagicTypeLineColorHandler(myChart, option);
    this.flushChartLayout(myChart);
    window.addEventListener('resize', () => myChart.resize());
  }

  private renderInitConsumptionChartDistributed(dom: HTMLElement, data: any) {
    const units = data[0].consumptionUnits || '';

    const existingChart = echarts.getInstanceByDom(dom);
    if (existingChart) existingChart.dispose();
    const myChart = echarts.init(dom);

    const narrow = dom.clientWidth < 768;
    const categories = data.map((item: any) => item.meterLocation);
    const values = data.map((item: any) => item.consumptionValueTotal);

    const periodLabel = `Desde el ${this.dateSpanishFormat(data[0].from)} hasta el ${this.dateSpanishFormat(data[0].to)}`;

    const option: echarts.EChartsOption = {
      title: {
        text: `Distribución de tu consumo por localizaciones`,
        left: narrow ? '5%' : 'center',
        top: '2%',
        ...(narrow && {
          width: dom.clientWidth * 0.45,
          overflow: 'break',
        }),
        textStyle: {
          color: '#1d1d1f',
          fontSize: narrow ? 14 : 18,
          fontWeight: 'bold', // Cambia "600" por 'bold'
          fontFamily: 'SF Pro Display, sans-serif',
        },
        subtext: `${periodLabel}`,
        subtextStyle: {
          color: this.getColorByType(),
        },
      },
      toolbox: {
        show: true,
        top: narrow ? '2%' : '0%',
        right: narrow ? '0%' : '5%',

        feature: {
          saveAsImage: { title: 'Descargar', name: 'Distribucion_Consumos' },
          magicType: { type: ['line', 'bar'], title: { line: 'Línea', bar: 'Barras' } },
          restore: { title: 'Reset' },
        },
      },
      grid: {
        top: '18%',
        bottom: '12%',
        left: '3%',
        right: '3%',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: 12,
        borderWidth: 0,
        padding: 12,
        shadowBlur: 10,
        shadowColor: 'rgba(0,0,0,0.1)',
        textStyle: { color: '#1d1d1f', fontFamily: 'SF Pro Display, sans-serif' },
        formatter: (params: any) => {
          const p = params[0];
          const item = data[p.dataIndex];
          const dynamicColor = this.getColorByType();
          return `
          <div style="color:#1d1d1f; margin-bottom:4px">Nº de contador: ${item.meterId}</div>
          <div style="font-weight:400; color:${dynamicColor}; margin-bottom:4px">${item.meterLocation}</div>
          <div style="font-weight:400; color:#86868b;">
            ${this.spanishFormat(item.consumptionValueTotal)} ${units}
          </div>
        `;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#86868b',
          interval: 0,
          rotate: data.length > 5 ? 30 : 0,
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f5f5f7' } },
        axisLabel: {
          color: '#86868b',
          formatter: (value: number) => `${this.spanishFormat(value)} ${units}`,
        },
      },
      series: [
        {
          name: 'Consumo por Ubicación',
          data: values,
          type: 'bar',
          barWidth: '40%',
          itemStyle: {
            color: this.getColorByType(),
            borderRadius: [6, 6, 0, 0],
          },
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(0, 0, 0, 0.02)',
            borderRadius: [6, 6, 0, 0],
          },
          label: {
            show: true,
            position: 'top',
            color: this.getColorByType(),
            fontWeight: 'bold',
            formatter: (p: any) => this.spanishFormat(p.value as number),
          },
        },
      ],
    };

    myChart.setOption(option);
    this.attachMagicTypeLineColorHandler(myChart, option);
    this.flushChartLayout(myChart);
    window.addEventListener('resize', () => myChart.resize());
  }
  createSummary(data: any[]) {
    if (!data || data.length === 0) {
      this.resumen = {
        avg: '0,00',
        maxV: '0,00',
        maxLabel: '---',
        minV: '0,00',
        minLabel: '---',
        total: '0,00',
        units: '',
      };
      this.cdr.detectChanges();
      return;
    }

    let total = 0;
    let maxItem = data[0];
    let minItem = data[0];

    data.forEach((item: any) => {
      const val = item.consumptionValue || 0;
      total += val;
      if (val > (maxItem.consumptionValue || 0)) maxItem = item;
      if (val < (minItem.consumptionValue || 0)) minItem = item;
    });

    const avg = total / data.length;

    this.resumen = {
      avg: this.spanishFormat(avg),
      total: this.spanishFormat(total),
      maxV: this.spanishFormat(maxItem.consumptionValue),
      maxLabel: this.formatByFrequency(this.dateSpanishFormat(maxItem.consumptionDate)),
      minLabel: this.formatByFrequency(this.dateSpanishFormat(minItem.consumptionDate)),
      minV: this.spanishFormat(minItem.consumptionValue),
      units: this.$readingsService.consumptionUnits() || '',
    };
    this.cdr.detectChanges();
  }
  createSummaryDistributed(data: any[]) {
    setTimeout(() => {
      if (!data || data.length === 0) {
        this.resumenDistributed = {
          avg: '0,00',
          maxV: '0,00',
          maxLabel: '---',
          minV: '0,00',
          minLabel: '---',
          total: '0,00',
          units: '',
        };
        return;
      }

      let total = 0;
      let maxItem = data[0];
      let minItem = data[0];

      data.forEach((item) => {
        const val = item.consumptionValueTotal || 0;
        total += val;
        if (val > (maxItem.consumptionValueTotal || 0)) {
          maxItem = item;
        }
        if (val < (minItem.consumptionValueTotal || 0)) {
          minItem = item;
        }
      });

      const units = data[0].consumptionUnits || '';

      this.resumenDistributed = {
        avg: this.spanishFormat(total / data.length),
        total: this.spanishFormat(total),
        maxV: this.spanishFormat(maxItem.consumptionValueTotal),
        maxLabel: maxItem.meterLocation || 'N/A',
        minV: this.spanishFormat(minItem.consumptionValueTotal),
        minLabel: minItem.meterLocation || 'N/A',
        units: units,
      };

      this.cdr.detectChanges();
    });
  }

  dateSpanishFormat(dateTime: string | Date | null | undefined): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  spanishFormat(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0,00';
    const numberValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue);
  }

  exportDisabled(): boolean {
    if (this.initialLoading() || this.loading || this.isDownloading()) {
      return true;
    }

    if (this.configForm?.controls['formatType']?.value === 'Consumo desglosado') {
      return (
        this.consumptionsTableByMeter().length === 0 && this.consumptionsDistributed().length === 0
      );
    }

    return this.consumptions().length === 0 && this.consumptionsTable().length === 0;
  }

  async exportConsumptions(format: ExportFormat): Promise<void> {
    if (this.exportDisabled()) {
      return;
    }

    this.isDownloading.set(true);
    try {
      const table = await this.buildConsumptionsExport();
      if (table.rows.length === 0) {
        return;
      }
      await downloadTableFile(table, format);
    } catch (error) {
      console.error('Error al exportar consumos:', error);
    } finally {
      this.isDownloading.set(false);
    }
  }

  private async buildConsumptionsExport(): Promise<ExportTable> {
    if (this.configForm.controls['formatType'].value === 'Consumo desglosado') {
      const rows = await this.loadDistributedExportRows();
      const units = this.consumptionsUnitsDistributed() || rows[0]?.consumptionUnits || '';
      return {
        filename: exportFilename('consumos-desglosado'),
        headers: ['Nº de contador', 'Localización', this.exportColumnLabel('Consumo', units)],
        rows: rows.map((item) => [
          exportTextCell(item.meterId == null ? '' : String(item.meterId)),
          exportTextCell(item.meterLocation ?? ''),
          exportNumberCell(
            item.consumptionValueTotal,
            this.spanishFormat(item.consumptionValueTotal),
          ),
        ]),
      };
    }

    const fullList = this.consumptions();
    const page = this.consumptionsTable();
    const source = fullList.length >= page.length ? fullList : page;
    const units = this.consumptionsUnits();
    return {
      filename: exportFilename('consumos'),
      headers: [
        'Fecha',
        this.exportColumnLabel('Lectura', units),
        this.exportColumnLabel('Consumo', units),
        'Tipo lectura',
      ],
      rows: [...source]
        .sort((left, right) =>
          this.compareConsumptionDateDesc(left.consumptionDate, right.consumptionDate),
        )
        .map((item) => [
          exportTextCell(this.dateSpanishFormat(item.consumptionDate)),
          exportNumberCell(item.readingValue, this.spanishFormat(item.readingValue)),
          exportNumberCell(item.consumptionValue, this.spanishFormat(item.consumptionValue)),
          exportTextCell(this.formatReadingType(item.readingType)),
        ]),
    };
  }

  private async loadDistributedExportRows() {
    const tenant = this.tenantSelected || (await this.$core.getTenantFromLocal());
    const supply = this.configForm.get('supplyType')?.value;
    const range = this.getSelectedDateRange();
    if (tenant?.tenantId && supply && range) {
      const rows = await this.$readingsService.fetchConsumptionsByMeterForExport(
        tenant.tenantId,
        this.getSupplyTypeId(supply),
        range.dateStart,
        range.dateEnd,
      );
      if (rows.length > 0) {
        return rows;
      }
    }

    const tableRows = this.consumptionsTableByMeter();
    return tableRows.length > 0 ? tableRows : this.consumptionsDistributed();
  }

  private exportColumnLabel(prefix: string, units: string): string {
    const trimmed = units.trim();
    return trimmed ? `${prefix} ${trimmed}` : prefix;
  }

  private compareConsumptionDateDesc(left: string, right: string): number {
    const leftTime = new Date(left).getTime();
    const rightTime = new Date(right).getTime();
    if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
      return right.localeCompare(left);
    }
    return rightTime - leftTime;
  }

  formatReadingType(value: string | null | undefined): string {
    if (!value) return '—';
    const labels: Record<string, string> = {
      FACILITADA: 'Facilitada',
      REAL: 'Real',
      ESTIMADA: 'Estimada',
    };
    const normalized = labels[value.toUpperCase()];
    if (normalized) return normalized;
    const lower = value.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  createDateGroup(): FormGroup {
    const hoy = new Date();
    const haceDosAnios = new Date();
    haceDosAnios.setFullYear(hoy.getFullYear() - 2);

    return this.fb.group({
      inicio: [haceDosAnios, Validators.required],
      fin: [hoy, Validators.required],
    });
  }
  private formatDate(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatByFrequency(dateStr: string): string {
    const parts = dateStr.split('/');
    if (parts.length < 3) return dateStr;
    const month = parts[1];
    const year = parts[2];
    return `${month}/${year}`;
  }
  addComparativa() {
    this.comparativas.push(this.createDateGroup());
  }

  removeComparativa(index: number) {
    this.comparativas.removeAt(index);
  }
  async onMeterSliderChange(event: any) {
    if (this.isSyncingSlider) return;
    const newIndex = event.page;
    this.currentPage.set(newIndex);

    const selectedMeter = this.filteredMeters()[newIndex];
    if (!selectedMeter) return;

    this.configForm.get('meter')?.setValue(selectedMeter, { emitEvent: false });

    const formatType = this.configForm.get('formatType')?.value;
    const supplyTypeId = this.configForm.get('supplyType')?.value?.supplyTypeId;
    const shouldLoadConsumption = supplyTypeId !== 14 || formatType === 'Consumo individual';
    if (shouldLoadConsumption) {
      await this.getConsumptions();
    }
  }

  getColorByType(): string {
    const typeId = this.configForm.controls['supplyType'].value;
    switch (typeId.supplyTypeId) {
      case 14: // repartidores
        return '#FFAB40'; // orangeAccent
      case 1: // agua fria
        return '#448AFF'; // blueAccent
      case 2: // Agua caliente
        return '#F44336'; // red
      case 3: // Calefacción
        return '#FFAB40'; // orangeAccent (radiadores)
      case 4: // Aire acondicionado
        return '#00BCD4'; // cyan
      default:
        return '#448AFF'; // blueAccent por defecto
    }
  }
  private lightenWithAlpha(hex: string, alpha = 0.6): string {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    const lighten = (c: number) => Math.min(255, Math.floor(c + (255 - c) * 0.2));
    const lr = lighten(r);
    const lg = lighten(g);
    const lb = lighten(b);
    return `rgba(${lr}, ${lg}, ${lb}, ${alpha})`;
  }
  getColorByTypeTransparency(): string {
    const typeId = this.configForm.controls['supplyType'].value;
    let baseColor = '';
    switch (typeId.supplyTypeId) {
      case 14: // repartidores
        baseColor = '#FFAB40';
        break;
      case 1: // agua fria
        baseColor = '#448AFF';
        break;
      case 2: // Agua caliente
        baseColor = '#F44336';
        break;
      case 3: // Calefacción
        baseColor = '#FFAB40';
        break;

      case 4: // Aire acondicionado
        baseColor = '#00BCD4';
        break;

      default:
        baseColor = '#448AFF';
    }
    return this.lightenWithAlpha(baseColor, 0.6);
  }
  getDateDaysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.formatDate(date);
  }

  getDateMonthsAgo(months: number): string {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return this.formatDate(date);
  }

  getDateYearsAgo(years: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - years);
    return this.formatDate(date);
  }

  formatByFrequencyGP(preSecondFormatedDate: any): string {
    const parts = preSecondFormatedDate?.toString().split('/');
    if (!parts || parts.length < 3) {
      return preSecondFormatedDate?.toString() || '';
    }
    const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return `${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  }

  clearSummarys() {
    this.resumen = {
      avg: '0,00',
      maxV: '0,00',
      maxLabel: '---',
      minV: '0,00',
      minLabel: '---',
      total: '0,00',
      units: '',
    };
    this.resumenDistributed = {
      avg: '0,00',
      maxV: '0,00',
      maxLabel: '---',
      minV: '0,00',
      minLabel: '---',
      total: '0,00',
      units: '',
    };
  }

  ngOnDestroy(): void {
    if (this.narrowViewportQuery && this.narrowViewportListener) {
      this.narrowViewportQuery.removeEventListener('change', this.narrowViewportListener);
    }
  }

  get consumptionsTableScrollable(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableScrollHeight(): string | undefined {
    if (this.isNarrowViewport() || this.useFixedConsumptionsTableRowHeight()) {
      return undefined;
    }
    return 'calc(-300px + 95vh)';
  }

  get consumptionsTableStyle(): Record<string, string> {
    if (this.isNarrowViewport()) {
      return { width: '100%' };
    }
    if (this.useFixedConsumptionsTableRowHeight()) {
      return { 'min-width': '50rem' };
    }
    return { 'min-width': '50rem', 'min-height': 'calc(-400px + 95vh)' };
  }

  get consumptionsTableStyleClass(): string | undefined {
    const classes: string[] = [];
    if (this.isNarrowViewport()) {
      classes.push('readings-consumptions-table-narrow');
    }
    if (this.useFixedConsumptionsTableRowHeight()) {
      classes.push('readings-table-rows-fixed');
    }
    return classes.length ? classes.join(' ') : undefined;
  }

  get consumptionsTableShowCurrentPageReport(): boolean {
    return true;
  }

  get consumptionsTableShowPageLinks(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableShowFirstLastIcon(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsTableRowsPerPageOptions(): number[] | undefined {
    return this.isNarrowViewport() ? undefined : [10, 20, 50, 100];
  }

  get consumptionsTablePaginatorStyleClass(): string | undefined {
    if (!this.isNarrowViewport()) {
      return undefined;
    }
    return 'w-100 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2';
  }

  get consumptionsByMeterTableScrollable(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsByMeterTableScrollHeight(): string | undefined {
    if (this.isNarrowViewport() || this.useFixedConsumptionsByMeterTableRowHeight()) {
      return undefined;
    }
    return 'calc(-300px + 95vh)';
  }

  get consumptionsByMeterTableStyle(): Record<string, string> {
    if (this.isNarrowViewport()) {
      return { width: '100%' };
    }
    if (this.useFixedConsumptionsByMeterTableRowHeight()) {
      return { 'min-width': '50rem' };
    }
    return { 'min-width': '50rem', 'min-height': 'calc(-400px + 95vh)' };
  }

  get consumptionsByMeterTableStyleClass(): string | undefined {
    const classes: string[] = [];
    if (this.isNarrowViewport()) {
      classes.push('readings-consumptions-table-narrow');
    }
    if (this.useFixedConsumptionsByMeterTableRowHeight()) {
      classes.push('readings-table-rows-fixed');
    }
    return classes.length ? classes.join(' ') : undefined;
  }

  get consumptionsByMeterTableShowCurrentPageReport(): boolean {
    return true;
  }

  get consumptionsByMeterTableShowPageLinks(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsByMeterTableShowFirstLastIcon(): boolean {
    return !this.isNarrowViewport();
  }

  get consumptionsByMeterTableRowsPerPageOptions(): number[] | undefined {
    return this.isNarrowViewport() ? undefined : [10, 20, 50, 100];
  }

  get consumptionsByMeterTablePaginatorStyleClass(): string | undefined {
    if (!this.isNarrowViewport()) {
      return undefined;
    }
    return 'w-100 d-flex flex-wrap justify-content-center align-items-center gap-2 py-2';
  }

  private initNarrowViewportListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.narrowViewportQuery = window.matchMedia('(max-width: 767px)');
    this.isNarrowViewport.set(this.narrowViewportQuery.matches);
    this.narrowViewportListener = (event: MediaQueryListEvent) => {
      this.isNarrowViewport.set(event.matches);
    };
    this.narrowViewportQuery.addEventListener('change', this.narrowViewportListener);
  }
}
